<template>
  <div class="third-party-log-page">
    <el-card shadow="never">
      <template #header>
        <div class="page-header">
          <div>
            <div class="page-title">第三方调用日志</div>
            <div class="page-subtitle">记录调用第三方厂商的入参、返回值、状态、失败原因和耗时，用于对账追踪。</div>
          </div>
        </div>
      </template>

      <el-form :model="search" label-width="92px" class="search-form">
        <el-row :gutter="12">
          <el-col :xs="24" :sm="12" :md="6">
            <el-form-item label="业务类型">
              <el-input v-model="search.productType" clearable placeholder="例如：出险 / 维保" @keyup.enter="searchData" />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :md="6">
            <el-form-item label="第三方厂商">
              <el-input v-model="search.thirdPartyName" clearable placeholder="例如：CHENGHAO" @keyup.enter="searchData" />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :md="6">
            <el-form-item label="开放产品编码">
              <el-input v-model="search.openApiProductCode" clearable placeholder="例如：50001" @keyup.enter="searchData" />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :md="6">
            <el-form-item label="VIN">
              <el-input v-model="search.vin" clearable placeholder="请输入VIN" @keyup.enter="searchData" />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :md="6">
            <el-form-item label="状态">
              <el-select v-model="search.invokeStatus" clearable placeholder="全部" style="width: 100%">
                <el-option label="成功" :value="1" />
                <el-option label="失败" :value="0" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="12">
          <el-col :xs="24" :sm="12" :md="6">
            <el-form-item label="订单ID">
              <el-input v-model="search.orderId" clearable placeholder="内部订单ID" @keyup.enter="searchData" />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :md="6">
            <el-form-item label="外部订单号">
              <el-input v-model="search.outerOrderId" clearable placeholder="第三方订单号" @keyup.enter="searchData" />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :md="6">
            <el-form-item label="接口名">
              <el-input v-model="search.apiName" clearable placeholder="接口方法名" @keyup.enter="searchData" />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :md="6">
            <el-form-item label="请求时间">
              <el-date-picker
                v-model="dateRange"
                type="datetimerange"
                range-separator="至"
                start-placeholder="开始时间"
                end-placeholder="结束时间"
                value-format="YYYY-MM-DD HH:mm:ss"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <div class="search-actions">
          <el-button type="primary" @click="searchData">查询</el-button>
          <el-button @click="resetSearch">重置</el-button>
        </div>
      </el-form>

      <el-table :data="tableData" border stripe height="calc(100vh - 390px)" v-loading="loading">
        <el-table-column prop="requestTime" label="请求时间" min-width="170" show-overflow-tooltip />
        <el-table-column prop="productType" label="业务类型" min-width="100" show-overflow-tooltip />
        <el-table-column prop="thirdPartyName" label="第三方厂商" min-width="130" show-overflow-tooltip />
        <el-table-column label="开放产品" min-width="190" show-overflow-tooltip>
          <template #default="scope">{{ openProductText(scope.row) }}</template>
        </el-table-column>
        <el-table-column prop="apiName" label="接口" min-width="220" show-overflow-tooltip />
        <el-table-column prop="vin" label="VIN" min-width="170" show-overflow-tooltip />
        <el-table-column prop="orderId" label="订单ID" min-width="170" show-overflow-tooltip />
        <el-table-column prop="outerOrderId" label="外部订单号" min-width="170" show-overflow-tooltip />
        <el-table-column label="状态" width="90" align="center">
          <template #default="scope">
            <el-tag :type="scope.row.invokeStatus === 1 ? 'success' : 'danger'">
              {{ scope.row.invokeStatus === 1 ? "成功" : "失败" }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="costTime" label="耗时(ms)" width="100" align="right" />
        <el-table-column prop="failReason" label="失败原因" min-width="180" show-overflow-tooltip />
        <el-table-column fixed="right" label="操作" width="90">
          <template #default="scope">
            <el-button size="small" @click="openDetail(scope.row)">详情</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-box">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next"
          v-model:current-page="page.current"
          v-model:page-size="page.size"
          :total="page.total"
          :page-sizes="[20, 50, 100]"
          @current-change="loadData"
          @size-change="handleSizeChange"
        />
      </div>
    </el-card>

    <el-dialog v-model="detail.visible" title="调用详情" width="900px">
      <el-descriptions :column="2" border>
        <el-descriptions-item label="业务类型">{{ detail.row.productType || "--" }}</el-descriptions-item>
        <el-descriptions-item label="第三方厂商">{{ detail.row.thirdPartyName || "--" }}</el-descriptions-item>
        <el-descriptions-item label="开放产品">{{ openProductText(detail.row) }}</el-descriptions-item>
        <el-descriptions-item label="接口">{{ detail.row.apiName || "--" }}</el-descriptions-item>
        <el-descriptions-item label="状态">{{ detail.row.invokeStatus === 1 ? "成功" : "失败" }}</el-descriptions-item>
        <el-descriptions-item label="订单ID">{{ detail.row.orderId || "--" }}</el-descriptions-item>
        <el-descriptions-item label="外部订单号">{{ detail.row.outerOrderId || "--" }}</el-descriptions-item>
        <el-descriptions-item label="VIN">{{ detail.row.vin || "--" }}</el-descriptions-item>
        <el-descriptions-item label="耗时">{{ detail.row.costTime || 0 }} ms</el-descriptions-item>
        <el-descriptions-item label="失败原因" :span="2">{{ detail.row.failReason || "--" }}</el-descriptions-item>
        <el-descriptions-item label="请求入参" :span="2"><pre class="json-box">{{ formatJson(detail.row.requestBody) }}</pre></el-descriptions-item>
        <el-descriptions-item label="返回值" :span="2"><pre class="json-box">{{ formatJson(detail.row.responseBody) }}</pre></el-descriptions-item>
      </el-descriptions>
    </el-dialog>
  </div>
</template>

<script setup>
import { onMounted, reactive, ref } from "vue";
import { getThirdPartyInvokeLogPage } from "@/api/thirdPartyInvokeLog";

const emptySearch = () => ({ productType: "", thirdPartyName: "", apiName: "", sourceType: "", openApiProductCode: "", orderId: "", outerOrderId: "", vin: "", invokeStatus: "" });
const search = reactive(emptySearch());
const dateRange = ref([]);
const tableData = ref([]);
const loading = ref(false);
const page = reactive({ current: 1, size: 20, total: 0 });
const detail = reactive({ visible: false, row: {} });

function openProductText(row) {
  if (!row?.openApiProductCode && !row?.openApiProductName) return "--";
  if (!row?.openApiProductName) return row.openApiProductCode;
  return row.openApiProductCode ? `${row.openApiProductName} / ${row.openApiProductCode}` : row.openApiProductName;
}

function params() {
  return { ...search, current: page.current, size: page.size, startTime: dateRange.value?.[0], endTime: dateRange.value?.[1] };
}

function loadData() {
  loading.value = true;
  getThirdPartyInvokeLogPage(params())
    .then((res) => {
      tableData.value = res.data?.records || [];
      page.total = res.data?.total || 0;
    })
    .finally(() => {
      loading.value = false;
    });
}

function searchData() {
  page.current = 1;
  loadData();
}

function resetSearch() {
  Object.assign(search, emptySearch());
  dateRange.value = [];
  searchData();
}

function handleSizeChange() {
  page.current = 1;
  loadData();
}

function openDetail(row) {
  detail.row = row || {};
  detail.visible = true;
}

function formatJson(value) {
  if (!value) return "--";
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch (e) {
    return value;
  }
}

onMounted(loadData);
</script>

<style scoped lang="scss">
.third-party-log-page { padding: 16px; }
.page-header { display: flex; align-items: center; justify-content: space-between; }
.page-title { font-size: 18px; font-weight: 600; color: #1f2937; }
.page-subtitle { margin-top: 4px; font-size: 12px; color: #8a94a6; }
.search-form { margin-bottom: 12px; }
.search-actions { display: flex; justify-content: flex-end; gap: 8px; }
.pagination-box { display: flex; justify-content: flex-end; padding-top: 12px; }
.json-box { white-space: pre-wrap; word-break: break-all; max-height: 280px; overflow: auto; margin: 0; font-family: Consolas, monospace; font-size: 12px; }
</style>